import React, { useEffect, useState } from 'react';
import { CalendarIcon, DollarSignIcon, RepeatIcon, SearchIcon, UserIcon, CreditCardIcon, ClockIcon, LinkIcon, MailIcon, PhoneIcon } from 'lucide-react';
interface RecurringPaymentProps {
  onAmountChange: (amount: string) => void;
  onCurrencyChange: (currency: string) => void;
  onReferenceChange: (reference: string) => void;
  onSubscriptionModeChange?: (mode: 'existing' | 'new') => void;
}
export function RecurringPayment({
  onAmountChange,
  onCurrencyChange,
  onReferenceChange,
  onSubscriptionModeChange
}: RecurringPaymentProps) {
  // Payment details
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  // Subscription details
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [billingCycles, setBillingCycles] = useState('');
  const [startDate, setStartDate] = useState('');
  const [trialDays, setTrialDays] = useState('0');
  // Customer selection
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<{
    id: string;
    name: string;
    email: string;
    cards: Array<{
      id: string;
      last4: string;
      brand: string;
      expiry: string;
    }>;
  } | null>(null);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  // Subscription mode
  const [subscriptionMode, setSubscriptionMode] = useState<'existing' | 'new'>('existing');
  // Customer email/phone for new subscription
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerName, setCustomerName] = useState('');
  // Notify parent component when subscription mode changes
  useEffect(() => {
    if (onSubscriptionModeChange) {
      onSubscriptionModeChange(subscriptionMode);
    }
  }, [subscriptionMode, onSubscriptionModeChange]);
  // Mock customer data
  const customers = [{
    id: 'cust_1',
    name: 'John Doe',
    email: 'john@example.com',
    cards: [{
      id: 'card_1',
      last4: '4242',
      brand: 'Visa',
      expiry: '04/25'
    }, {
      id: 'card_2',
      last4: '5555',
      brand: 'Mastercard',
      expiry: '07/24'
    }]
  }, {
    id: 'cust_2',
    name: 'Jane Smith',
    email: 'jane@example.com',
    cards: [{
      id: 'card_3',
      last4: '1234',
      brand: 'Visa',
      expiry: '09/26'
    }]
  }];
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d.]/g, '');
    setAmount(value);
    onAmountChange(value);
  };
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrency(e.target.value);
    onCurrencyChange(e.target.value);
  };
  const handleReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReference(e.target.value);
    onReferenceChange(e.target.value);
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setSelectedCustomer(null);
    setSelectedCard(null);
  };
  const handleSelectCustomer = (customer: (typeof customers)[0]) => {
    setSelectedCustomer(customer);
    setSelectedCard(null);
    setSearchTerm('');
  };
  const handleSelectCard = (cardId: string) => {
    setSelectedCard(cardId);
  };
  const handleSubscriptionModeChange = (mode: 'existing' | 'new') => {
    setSubscriptionMode(mode);
    if (onSubscriptionModeChange) {
      onSubscriptionModeChange(mode);
    }
  };
  // Filter customers based on search term
  const filteredCustomers = searchTerm ? customers.filter(customer => customer.name.toLowerCase().includes(searchTerm.toLowerCase()) || customer.email.toLowerCase().includes(searchTerm.toLowerCase())) : [];
  return <div className="space-y-6" data-id="element-1520">
      {/* Subscription Mode Selection */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1521">
        <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-1522">
          <RepeatIcon size={20} className="mr-2 text-blue-600" data-id="element-1523" />
          Subscription Type
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1524">
          <div className={`border rounded-lg p-4 cursor-pointer transition-all ${subscriptionMode === 'existing' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'}`} onClick={() => handleSubscriptionModeChange('existing')} data-id="element-1525">
            <div className="flex items-start space-x-3" data-id="element-1526">
              <div className="p-2 bg-blue-100 rounded-md" data-id="element-1527">
                <CreditCardIcon size={20} className="text-blue-600" data-id="element-1528" />
              </div>
              <div data-id="element-1529">
                <h3 className="font-medium text-gray-900" data-id="element-1530">Existing Customer</h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-1531">
                  Create a subscription for an existing customer with a saved
                  payment method
                </p>
              </div>
            </div>
          </div>
          <div className={`border rounded-lg p-4 cursor-pointer transition-all ${subscriptionMode === 'new' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'}`} onClick={() => handleSubscriptionModeChange('new')} data-id="element-1532">
            <div className="flex items-start space-x-3" data-id="element-1533">
              <div className="p-2 bg-blue-100 rounded-md" data-id="element-1534">
                <LinkIcon size={20} className="text-blue-600" data-id="element-1535" />
              </div>
              <div data-id="element-1536">
                <h3 className="font-medium text-gray-900" data-id="element-1537">
                  New Subscription Link
                </h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-1538">
                  Send a link to customer to start a new subscription
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Customer Selection (for existing customers) */}
      {subscriptionMode === 'existing' && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1539">
          <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1540">
            Customer Selection
          </h3>
          <div className="space-y-4" data-id="element-1541">
            <div data-id="element-1542">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1543">
                Search Customer
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-1544">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1545">
                  <SearchIcon size={16} className="text-gray-400" data-id="element-1546" />
                </div>
                <input type="text" value={searchTerm} onChange={handleSearchChange} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Search by name or email" data-id="element-1547" />
              </div>
            </div>
            {searchTerm && filteredCustomers.length > 0 && !selectedCustomer && <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-1548">
                  <ul className="divide-y divide-gray-200" data-id="element-1549">
                    {filteredCustomers.map(customer => <li key={customer.id} className="hover:bg-gray-50" data-id="element-1550">
                        <button type="button" onClick={() => handleSelectCustomer(customer)} className="w-full px-4 py-3 text-left flex items-center" data-id="element-1551">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mr-3" data-id="element-1552">
                            <UserIcon size={16} data-id="element-1553" />
                          </div>
                          <div data-id="element-1554">
                            <p className="text-sm font-medium text-gray-900" data-id="element-1555">
                              {customer.name}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-1556">
                              {customer.email}
                            </p>
                          </div>
                        </button>
                      </li>)}
                  </ul>
                </div>}
            {selectedCustomer && <div className="bg-gray-50 rounded-md p-4" data-id="element-1557">
                <div className="flex items-center justify-between" data-id="element-1558">
                  <div className="flex items-center" data-id="element-1559">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mr-3" data-id="element-1560">
                      <UserIcon size={20} data-id="element-1561" />
                    </div>
                    <div data-id="element-1562">
                      <p className="text-sm font-medium text-gray-900" data-id="element-1563">
                        {selectedCustomer.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-1564">
                        {selectedCustomer.email}
                      </p>
                    </div>
                  </div>
                  <button type="button" onClick={() => setSelectedCustomer(null)} className="text-sm text-blue-600 hover:text-blue-800" data-id="element-1565">
                    Change
                  </button>
                </div>
                <div className="mt-4" data-id="element-1566">
                  <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-1567">
                    Saved Payment Methods
                  </p>
                  <div className="space-y-2" data-id="element-1568">
                    {selectedCustomer.cards.map(card => <div key={card.id} onClick={() => handleSelectCard(card.id)} className={`border rounded-md p-3 cursor-pointer ${selectedCard === card.id ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-300 hover:bg-blue-50/50'}`} data-id="element-1569">
                        <div className="flex items-center justify-between" data-id="element-1570">
                          <div className="flex items-center" data-id="element-1571">
                            <div className="mr-3" data-id="element-1572">
                              {card.brand === 'Visa' ? <CreditCardIcon size={20} className="text-blue-700" data-id="element-1573" /> : card.brand === 'Mastercard' ? <CreditCardIcon size={20} className="text-red-600" data-id="element-1574" /> : <CreditCardIcon size={20} className="text-gray-600" data-id="element-1575" />}
                            </div>
                            <div data-id="element-1576">
                              <p className="text-sm font-medium text-gray-900" data-id="element-1577">
                                {card.brand} •••• {card.last4}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-1578">
                                Expires {card.expiry}
                              </p>
                            </div>
                          </div>
                          <div className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center" data-id="element-1579">
                            {selectedCard === card.id && <div className="w-3 h-3 rounded-full bg-blue-600" data-id="element-1580"></div>}
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>}
          </div>
        </div>}
      {/* Customer Information (for new subscription) */}
      {subscriptionMode === 'new' && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1581">
          <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1582">
            Customer Information
          </h3>
          <div className="space-y-4" data-id="element-1583">
            <div data-id="element-1584">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1585">
                Customer Name (Optional)
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-1586">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1587">
                  <UserIcon size={16} className="text-gray-400" data-id="element-1588" />
                </div>
                <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="John Doe" data-id="element-1589" />
              </div>
            </div>
            <div data-id="element-1590">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1591">
                Customer Email
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-1592">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1593">
                  <MailIcon size={16} className="text-gray-400" data-id="element-1594" />
                </div>
                <input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="customer@example.com" required data-id="element-1595" />
              </div>
            </div>
            <div data-id="element-1596">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1597">
                Customer Phone (Optional)
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-1598">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1599">
                  <PhoneIcon size={16} className="text-gray-400" data-id="element-1600" />
                </div>
                <input type="tel" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="+1 (555) 123-4567" data-id="element-1601" />
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-1602">
                We'll send a subscription link to the customer via email or SMS
              </p>
            </div>
          </div>
        </div>}
      {/* Subscription Details */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1603">
        <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-1604">
          <RepeatIcon size={20} className="mr-2 text-blue-600" data-id="element-1605" />
          Subscription Details
        </h3>
        <div className="space-y-4" data-id="element-1606">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1607">
            <div data-id="element-1608">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1609">
                Amount per Billing Period*
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-1610">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1611">
                  <DollarSignIcon size={16} className="text-gray-400" data-id="element-1612" />
                </div>
                <input type="text" value={amount} onChange={handleAmountChange} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="0.00" required data-id="element-1613" />
              </div>
            </div>
            <div data-id="element-1614">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1615">
                Currency
              </label>
              <select value={currency} onChange={handleCurrencyChange} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-1616">
                <option value="USD" data-id="element-1617">USD - US Dollar</option>
                <option value="EUR" data-id="element-1618">EUR - Euro</option>
                <option value="GBP" data-id="element-1619">GBP - British Pound</option>
                <option value="JPY" data-id="element-1620">JPY - Japanese Yen</option>
                <option value="CAD" data-id="element-1621">CAD - Canadian Dollar</option>
                <option value="AUD" data-id="element-1622">AUD - Australian Dollar</option>
              </select>
            </div>
          </div>
          <div data-id="element-1623">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1624">
              Description*
            </label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Premium Membership" required data-id="element-1625" />
          </div>
          <div data-id="element-1626">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1627">
              Your Reference (Optional)
            </label>
            <input type="text" value={reference} onChange={handleReferenceChange} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Internal reference" data-id="element-1628" />
          </div>
        </div>
      </div>
      {/* Billing Schedule */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1629">
        <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-1630">
          <ClockIcon size={20} className="mr-2 text-blue-600" data-id="element-1631" />
          Billing Schedule
        </h3>
        <div className="space-y-4" data-id="element-1632">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1633">
            <div data-id="element-1634">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1635">
                Billing Interval
              </label>
              <select value={billingInterval} onChange={e => setBillingInterval(e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-1636">
                <option value="weekly" data-id="element-1637">Weekly</option>
                <option value="biweekly" data-id="element-1638">Every 2 Weeks</option>
                <option value="monthly" data-id="element-1639">Monthly</option>
                <option value="quarterly" data-id="element-1640">Quarterly</option>
                <option value="biannually" data-id="element-1641">Every 6 Months</option>
                <option value="annually" data-id="element-1642">Annually</option>
              </select>
            </div>
            <div data-id="element-1643">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1644">
                Number of Billing Cycles (Optional)
              </label>
              <input type="number" min="1" value={billingCycles} onChange={e => setBillingCycles(e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Leave blank for unlimited" data-id="element-1645" />
              <p className="mt-1 text-xs text-gray-500" data-id="element-1646">
                Leave blank for an ongoing subscription
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1647">
            <div data-id="element-1648">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1649">
                Start Date
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-1650">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1651">
                  <CalendarIcon size={16} className="text-gray-400" data-id="element-1652" />
                </div>
                <input type="text" value={startDate} onChange={e => setStartDate(e.target.value)} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="MM/DD/YYYY" data-id="element-1653" />
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-1654">
                Leave blank to start immediately
              </p>
            </div>
            <div data-id="element-1655">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1656">
                Trial Period (Days)
              </label>
              <input type="number" min="0" value={trialDays} onChange={e => setTrialDays(e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="0" data-id="element-1657" />
              <p className="mt-1 text-xs text-gray-500" data-id="element-1658">
                Free trial before first payment
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Subscription Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-1659">
        <h3 className="text-md font-medium text-gray-800 mb-2" data-id="element-1660">
          Subscription Summary
        </h3>
        <div className="space-y-2" data-id="element-1661">
          <div className="flex justify-between" data-id="element-1662">
            <span className="text-sm text-gray-600" data-id="element-1663">Amount:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-1664">
              {amount ? `$${amount} ${currency}` : '-'}
            </span>
          </div>
          <div className="flex justify-between" data-id="element-1665">
            <span className="text-sm text-gray-600" data-id="element-1666">Billing Interval:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-1667">
              {billingInterval === 'weekly' ? 'Weekly' : billingInterval === 'biweekly' ? 'Every 2 Weeks' : billingInterval === 'monthly' ? 'Monthly' : billingInterval === 'quarterly' ? 'Every 3 Months' : billingInterval === 'biannually' ? 'Every 6 Months' : 'Annually'}
            </span>
          </div>
          <div className="flex justify-between" data-id="element-1668">
            <span className="text-sm text-gray-600" data-id="element-1669">Duration:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-1670">
              {billingCycles ? `${billingCycles} billing cycles` : 'Ongoing'}
            </span>
          </div>
          {parseInt(trialDays) > 0 && <div className="flex justify-between" data-id="element-1671">
              <span className="text-sm text-gray-600" data-id="element-1672">Trial Period:</span>
              <span className="text-sm font-medium text-gray-800" data-id="element-1673">
                {trialDays} days
              </span>
            </div>}
          <div className="flex justify-between" data-id="element-1674">
            <span className="text-sm text-gray-600" data-id="element-1675">Start Date:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-1676">
              {startDate || 'Immediate'}
            </span>
          </div>
          <div className="flex justify-between" data-id="element-1677">
            <span className="text-sm text-gray-600" data-id="element-1678">Customer:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-1679">
              {subscriptionMode === 'existing' ? selectedCustomer ? selectedCustomer.name : 'Not selected' : customerEmail ? customerName || customerEmail : 'Not specified'}
            </span>
          </div>
          {subscriptionMode === 'existing' && selectedCustomer && selectedCard && <div className="flex justify-between" data-id="element-1680">
                <span className="text-sm text-gray-600" data-id="element-1681">Payment Method:</span>
                <span className="text-sm font-medium text-gray-800" data-id="element-1682">
                  {selectedCustomer.cards.find(card => card.id === selectedCard)?.brand}{' '}
                  ••••
                  {selectedCustomer.cards.find(card => card.id === selectedCard)?.last4}
                </span>
              </div>}
        </div>
      </div>
    </div>;
}